import type { AudioFrame } from "@/utils/arcade/beat";
import { Fx } from "./fx";
import { createProgression } from "./progression";
import { createRhythmChart, hitGain, leadFor, missCost, type ChartNote } from "./rhythmChart";
import { drawBeatFlash, drawCoverBackdrop, drawGlow, drawSpectrumRing, drawVinyl, glowText, withAlpha, type Ctx2D } from "./render";
import { createFeedbackQueue, createSpawnWatcher, EMPTY_INPUT, type FrameInput, type GameEngine, type GameEnv, type GameStatus } from "./types";

interface LaneNote {
  lane: number;
  due: number;
  judged: boolean;
  end?: number;
}

interface Hold {
  lane: number;
  end: number;
}

export const LANE_COUNT = 4;

const LANE_KEYS: Record<string, number> = {
  KeyD: 0,
  KeyF: 1,
  KeyJ: 2,
  KeyK: 3,
  ArrowLeft: 0,
  ArrowDown: 1,
  ArrowUp: 2,
  ArrowRight: 3,
  Digit1: 0,
  Digit2: 1,
  Digit3: 2,
  Digit4: 3,
};

const KEY_LABELS = ["D", "F", "J", "K"];
const LANE_CODES = ["KeyD", "KeyF", "KeyJ", "KeyK"];
const PERFECT_SECONDS = 0.065;
const GOOD_SECONDS = 0.16;

// A note highway in 3D. Notes stream out of the spinning cover art at the
// vanishing point, one per beat in the lane of whichever band jumped the most.
export function createBeatLanes(env: GameEnv): GameEngine {
  const { theme, rng, difficulty, art } = env;
  const { palette } = theme;
  const fx = new Fx(rng, env.reducedMotion, art, env.quiet);
  const progression = createProgression(fx, palette, ["Hold notes: keep the lane held", "Chords and faster notes", "Everything, faster"]);
  let holds: Hold[] = [];
  let width = env.width;
  let height = env.height;

  let time = 0;
  const judgeTime = () => time - (env.controls?.timingOffsetMs ?? 0) / 1000;
  let score = 0;
  let combo = 0;
  let health = 1;
  let over = false;
  let notes: LaneNote[] = [];
  const chart = createRhythmChart(rng, difficulty);
  let lastLevel = 1;
  const bandAverages = [0.3, 0.3, 0.3, 0.3];
  let lastLane = -1;
  let repeats = 0;
  const flashes = new Array(LANE_COUNT).fill(0);
  const beams = new Array(LANE_COUNT).fill(0);
  const judgement = { label: "", life: 0, color: palette.text };
  const watcher = createSpawnWatcher();
  const feedbackQueue = createFeedbackQueue();
  let damage = 0;

  const travel = () => leadFor(1.9, progression.level, difficulty, 1.0);
  const hitY = () => height * 0.86;
  const vanish = () => ({ x: width / 2, y: height * 0.24 });
  const bottomWidth = () => Math.min(width * 0.96, height * 0.95, 680);
  const topWidth = () => bottomWidth() * 0.12;

  // Depth runs 0 at the vanishing point to 1 at the hit line, with perspective.
  const project = (depth: number, laneOffset: number) => {
    const v = vanish();
    const eased = depth * depth * 0.55 + depth * 0.45;
    const y = v.y + (hitY() - v.y) * eased;
    const span = topWidth() + (bottomWidth() - topWidth()) * eased;
    return { x: v.x + (laneOffset - LANE_COUNT / 2) * (span / LANE_COUNT), y, scale: 0.12 + eased * 0.88, span };
  };

  const laneBusy = (lane: number, at: number) => notes.some((note) => note.lane === lane && (Math.abs(note.due - at) < 0.12 || (note.end !== undefined && note.due <= at && note.end >= at - 0.05)));

  // Picks the lane for a charted note: whichever band is most above its own
  // average right now, never the same lane three times running, never into a
  // lane that is busy with a hold.
  const pickLane = (audio: AudioFrame, at: number, avoid = -1) => {
    const levels = [audio.kick, audio.bass, audio.mid, audio.treble];
    const weights = levels.map((value, i) => value / (bandAverages[i] + 0.05) + rng() * 0.9);
    for (let i = 0; i < LANE_COUNT; i++) {
      if (i === avoid || laneBusy(i, at)) weights[i] = -Infinity;
    }
    if (repeats >= 1 && lastLane >= 0 && weights.some((w, i) => i !== lastLane && w > -Infinity)) weights[lastLane] = -Infinity;
    let lane = -1;
    for (let i = 0; i < LANE_COUNT; i++) if (weights[i] > -Infinity && (lane < 0 || weights[i] > weights[lane])) lane = i;
    return lane;
  };

  const place = (step: ChartNote, audio: AudioFrame) => {
    const lane = pickLane(audio, step.at);
    if (lane < 0) return;
    repeats = lane === lastLane ? repeats + 1 : 0;
    lastLane = lane;
    notes.push({ lane, due: step.at, judged: false, end: step.hold > 0 ? step.at + step.hold : undefined });
    if (step.chord) {
      const second = pickLane(audio, step.at, lane);
      if (second >= 0) notes.push({ lane: second, due: step.at, judged: false });
    }
  };

  const judge = (label: string, color: string) => {
    judgement.label = label;
    judgement.color = color;
    judgement.life = 0.55;
  };

  const press = (lane: number) => {
    flashes[lane] = 1;
    let best: LaneNote | null = null;
    for (const note of notes) {
      if (note.judged || note.lane !== lane) continue;
      const offset = Math.abs(note.due - judgeTime());
      if (offset <= GOOD_SECONDS && (!best || offset < Math.abs(best.due - judgeTime()))) best = note;
    }
    if (!best) return;
    best.judged = true;
    if (best.end) holds.push({ lane, end: best.end });
    const perfect = Math.abs(best.due - judgeTime()) <= PERFECT_SECONDS;
    combo += 1;
    health = Math.min(1, health + hitGain(Math.abs(best.due - judgeTime()) <= PERFECT_SECONDS, progression.level));
    feedbackQueue.push("tap");
    const multiplier = Math.min(4, 1 + Math.floor(combo / 10) * 0.5) * progression.multiplier;
    score += Math.round((perfect ? 100 : 60) * multiplier);
    beams[lane] = 1;
    const at = project(1, lane + 0.5);
    fx.burst(at.x, at.y, palette.lanes[lane], perfect ? 22 : 12, perfect ? 260 : 170, 60);
    fx.ring(at.x, at.y, palette.lanes[lane], at.span / LANE_COUNT);
    judge(perfect ? "PERFECT" : "GOOD", perfect ? palette.accent : palette.text);
  };

  const laneFromX = (x: number, y: number) => {
    const depth = Math.max(0.6, Math.min(1, (y - vanish().y) / (hitY() - vanish().y)));
    const row = project(depth, 0);
    const left = row.x;
    return Math.max(0, Math.min(LANE_COUNT - 1, Math.floor((x - left) / (row.span / LANE_COUNT))));
  };

  return {
    bloom: 0.55,

    resize(nextWidth, nextHeight) {
      width = nextWidth;
      height = nextHeight;
    },

    autopilot() {
      const pressed = new Set<string>();
      const held = new Set<string>(holds.map((hold) => LANE_CODES[hold.lane]));
      for (const note of notes) {
        if (!note.judged && Math.abs(note.due - judgeTime()) < 0.02) pressed.add(LANE_CODES[note.lane]);
      }
      return pressed.size || held.size ? { ...EMPTY_INPUT, pressed, held } : EMPTY_INPUT;
    },

    update(dt, input: FrameInput, audio) {
      for (let i = 0; i < LANE_COUNT; i++) {
        flashes[i] = Math.max(0, flashes[i] - dt * 4);
        beams[i] = Math.max(0, beams[i] - dt * 2.5);
      }
      judgement.life = Math.max(0, judgement.life - dt);
      if (over) {
        fx.update(dt);
        return;
      }
      time += dt;
      const levels = [audio.kick, audio.bass, audio.mid, audio.treble];
      for (let i = 0; i < LANE_COUNT; i++) bandAverages[i] += (levels[i] - bandAverages[i]) * Math.min(1, dt * 1.5);

      if (audio.beat) {
        // The tick happened sinceBeat ms ago; chart the beat one lead ahead.
        const tickAt = time - audio.sinceBeat / 1000;
        for (const step of chart.onBeat(tickAt, audio.beatPeriod / 1000, travel(), progression.level, audio.energy, audio.musicCue?.(Math.ceil(travel() / (audio.beatPeriod / 1000)) * audio.beatPeriod / 1000))) place(step, audio);
      }

      for (const tap of input.taps) press(laneFromX(tap.x, tap.y));
      for (const code of input.pressed) {
        if (code in LANE_KEYS) press(LANE_KEYS[code]);
      }

      // Hold notes: the lane must stay held (key or finger) until the tail.
      const heldLanes = new Set<number>();
      for (const code of input.held) if (code in LANE_KEYS) heldLanes.add(LANE_KEYS[code]);
      // A press this frame is a finger or key that is down right now, even if
      // the held set has not caught up yet; without this a hold broke the
      // instant it started.
      for (const code of input.pressed) if (code in LANE_KEYS) heldLanes.add(LANE_KEYS[code]);
      for (const tap of input.taps) heldLanes.add(laneFromX(tap.x, tap.y));
      if (input.pointer?.down) heldLanes.add(laneFromX(input.pointer.x, input.pointer.y));
      for (const hold of holds) {
        if (judgeTime() >= hold.end) {
          score += 100 * progression.multiplier;
          const at = project(1, hold.lane + 0.5);
          fx.burst(at.x, at.y, palette.lanes[hold.lane], 18, 220, 40);
          fx.text(at.x, at.y - 40, "HOLD +100", palette.accent);
          hold.end = -1;
        } else if (heldLanes.has(hold.lane)) {
          score += dt * 160 * progression.multiplier;
          beams[hold.lane] = 1;
          flashes[hold.lane] = Math.max(flashes[hold.lane], 0.6);
          if (rng() < 0.4) {
            const at = project(1, hold.lane + 0.5);
            fx.trail(at.x + (rng() - 0.5) * 20, at.y, palette.lanes[hold.lane], 0, -200);
          }
        } else if (hold.end - judgeTime() > 0.1) {
          combo = 0;
          health -= missCost(progression.level) * 0.5;
          damage += 1;
          feedbackQueue.push("hurt");
          judge("BROKEN", palette.danger);
          hold.end = -1;
        } else {
          hold.end = -1;
        }
      }
      holds = holds.filter((hold) => hold.end > 0);
      progression.update(dt, time, combo);
      if (progression.level !== lastLevel) {
        lastLevel = progression.level;
        feedbackQueue.push("level");
      }

      for (const note of notes) {
        if (!note.judged && judgeTime() - note.due > GOOD_SECONDS) {
          note.judged = true;
          combo = 0;
          health -= missCost(progression.level);
          damage += 1;
          feedbackQueue.push("hurt");
          judge("MISS", palette.danger);
          fx.kick(4);
        }
      }
      notes = notes.filter((note) => !note.judged && time - note.due < 0.5);
      score = Math.round(score);
      if (health <= 0) {
        health = 0;
        over = true;
      }
      fx.update(dt);
    },

    draw(ctx, audio) {
      drawCoverBackdrop(ctx, art, palette, width, height, { alpha: 0.5, drift: time * 0.2 });
      ctx.save();
      fx.applyShake(ctx);
      const v = vanish();

      const recordR = Math.min(width, height) * 0.085;
      drawSpectrumRing(ctx, audio, v.x, v.y, recordR, recordR * 1.1, time * 0.2, [palette.primary, palette.accent]);
      drawVinyl(ctx, art, palette, v.x, v.y, recordR * (1 + audio.pulse * 0.05), time * 1.6, 0.9);

      const near = project(1.08, 0);
      const far = project(0, 0);
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(far.x, far.y);
      ctx.lineTo(far.x + far.span, far.y);
      ctx.lineTo(near.x + near.span, near.y);
      ctx.lineTo(near.x, near.y);
      ctx.closePath();
      const road = ctx.createLinearGradient(0, far.y, 0, near.y);
      road.addColorStop(0, withAlpha(palette.backgroundDeep, 0.2));
      road.addColorStop(1, withAlpha(palette.backgroundDeep, 0.9));
      ctx.fillStyle = road;
      ctx.fill();
      ctx.clip();

      for (let i = 0; i < LANE_COUNT; i++) {
        const light = flashes[i] * 0.5 + beams[i] * 0.5;
        if (light <= 0.02) continue;
        const a = project(0, i);
        const b = project(0, i + 1);
        const c = project(1.08, i + 1);
        const d = project(1.08, i);
        const beam = ctx.createLinearGradient(0, far.y, 0, near.y);
        beam.addColorStop(0, withAlpha(palette.lanes[i], 0));
        beam.addColorStop(1, withAlpha(palette.lanes[i], 0.5 * light));
        ctx.fillStyle = beam;
        ctx.globalCompositeOperation = "lighter";
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.lineTo(c.x, c.y);
        ctx.lineTo(d.x, d.y);
        ctx.closePath();
        ctx.fill();
        ctx.globalCompositeOperation = "source-over";
      }

      const scrollPhase = (time / (60 / Math.max(60, theme.bpm))) % 1;
      ctx.globalCompositeOperation = "lighter";
      for (let i = 0; i < 10; i++) {
        const depth = (i + scrollPhase) / 10;
        const left = project(depth, 0);
        ctx.strokeStyle = withAlpha(palette.text, 0.03 + depth * 0.1);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(left.x, left.y);
        ctx.lineTo(left.x + left.span, left.y);
        ctx.stroke();
      }
      for (let i = 0; i <= LANE_COUNT; i++) {
        const top = project(0, i);
        const bottom = project(1.08, i);
        const edge = i === 0 || i === LANE_COUNT;
        ctx.strokeStyle = withAlpha(edge ? palette.primary : palette.text, edge ? 0.85 : 0.18);
        ctx.lineWidth = edge ? 3 : 1;
        ctx.beginPath();
        ctx.moveTo(top.x, top.y);
        ctx.lineTo(bottom.x, bottom.y);
        ctx.stroke();
      }
      ctx.restore();

      for (const edge of [0, LANE_COUNT]) {
        const bottom = project(1, edge);
        drawGlow(ctx, art, palette.primary, bottom.x, bottom.y, 40 + audio.pulse * 20, 0.7);
      }

      const target = project(1, 0);
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.fillStyle = withAlpha(palette.text, 0.35 + audio.pulse * 0.45);
      ctx.fillRect(target.x, target.y - 2, target.span, 4);
      ctx.restore();
      for (let i = 0; i < LANE_COUNT; i++) {
        const at = project(1, i + 0.5);
        const r = Math.min(at.span / LANE_COUNT * 0.34, 34) * (1 + flashes[i] * 0.2);
        drawGlow(ctx, art, palette.lanes[i], at.x, at.y, r * 2.4, 0.35 + flashes[i] * 0.6);
        ctx.beginPath();
        ctx.arc(at.x, at.y, r, 0, Math.PI * 2);
        ctx.strokeStyle = palette.lanes[i];
        ctx.lineWidth = 3;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(at.x, at.y, r * 0.55, 0, Math.PI * 2);
        ctx.fillStyle = withAlpha(palette.lanes[i], 0.18 + flashes[i] * 0.5);
        ctx.fill();
        if (env.keyHints && !env.quiet) {
          ctx.fillStyle = withAlpha(palette.text, 0.7);
          ctx.font = "700 13px Poppins, sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(KEY_LABELS[i], at.x, at.y + r + 18);
        }
      }

      const laneColor = (lane: number) => (progression.fever ? palette.accent : palette.lanes[lane]);
      const drawTail = (lane: number, fromDepth: number, toDepth: number, alpha: number) => {
        const a = project(Math.max(0, Math.min(1, fromDepth)), lane + 0.35);
        const b = project(Math.max(0, Math.min(1, fromDepth)), lane + 0.65);
        const c = project(Math.max(0, Math.min(1, toDepth)), lane + 0.65);
        const d = project(Math.max(0, Math.min(1, toDepth)), lane + 0.35);
        ctx.save();
        ctx.globalCompositeOperation = "lighter";
        ctx.fillStyle = withAlpha(laneColor(lane), alpha);
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.lineTo(c.x, c.y);
        ctx.lineTo(d.x, d.y);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      };
      for (const hold of holds) drawTail(hold.lane, 1 - (hold.end - time) / travel(), 1, 0.55 + audio.pulse * 0.3);
      for (const note of notes) {
        if (note.end) drawTail(note.lane, 1 - (note.end - time) / travel(), 1 - (note.due - time) / travel(), 0.32);
      }

      const sorted = [...notes].sort((a, b) => b.due - a.due);
      for (const note of sorted) {
        const depth = 1 - (note.due - time) / travel();
        if (depth < 0 || depth > 1.1) continue;
        const at = project(depth, note.lane + 0.5);
        const noteWidth = (at.span / LANE_COUNT) * 0.7;
        const noteHeight = Math.max(4, 22 * at.scale);
        const color = laneColor(note.lane);
        drawGlow(ctx, art, color, at.x, at.y, noteWidth * 0.9, 0.5 + depth * 0.4);
        ctx.fillStyle = color;
        roundedBar(ctx, at.x - noteWidth / 2, at.y - noteHeight / 2, noteWidth, noteHeight);
        ctx.fill();
        ctx.fillStyle = "rgba(255,255,255,0.75)";
        roundedBar(ctx, at.x - noteWidth / 2 + noteWidth * 0.12, at.y - noteHeight / 2 + noteHeight * 0.18, noteWidth * 0.76, Math.max(1.5, noteHeight * 0.22));
        ctx.fill();
      }

      if (judgement.life > 0 && !env.quiet) {
        const at = project(0.62, LANE_COUNT / 2);
        glowText(ctx, judgement.label, at.x, at.y, Math.min(34, width * 0.07), judgement.color, Math.min(1, judgement.life * 3));
        if (combo > 2) glowText(ctx, `${combo} COMBO`, at.x, at.y + 30, 14, palette.primary, Math.min(1, judgement.life * 3));
      }

      fx.draw(ctx);
      ctx.restore();
      fx.drawBanner(ctx, width, height);
      drawBeatFlash(ctx, width, height, progression.fever ? palette.accent : palette.primary, audio.pulse * (progression.fever ? 0.9 : 0.5) + fx.flash);
    },

    feedback() {
      return feedbackQueue.drain();
    },

    probe() {
      let threats = 0;
      for (const note of notes) {
        watcher.see(note.end ? "hold" : "note", note);
        if (!note.judged && note.due - time <= travel()) threats += 1;
      }
      return { threats, pickups: 0, spawned: watcher.spawned, damage, intensity: chart.density(time) * (1.9 / travel()), boss: false, mission: null };
    },

    status(): GameStatus {
      return { score, lives: 0, maxLives: 0, combo, meter: health, over, level: progression.level, fever: progression.fever };
    },
  };
}

function roundedBar(ctx: Ctx2D, x: number, y: number, w: number, h: number) {
  const r = Math.min(h / 2, w / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
